import React, { useState } from 'react';
import {
    Box,
    Dialog,
    Button,
    IconButton,
    DialogTitle,
    DialogContent,
    InputAdornment,
    TextField,
} from '@mui/material';
import { Eye, EyeOff, X } from 'react-feather';
import { useDispatch } from 'react-redux';
import { SET_ALERT } from 'context/actions/system';
import { useTranslation } from 'react-i18next';
import useWindowSize from 'utils/useWindowSize';
import { changePassword } from 'context/actions/accounts';

const PasswordChangeCompose = ({ open, handleClose, onSave }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { sizes } = useWindowSize();

    // ✅ States
    const [oldPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');

    // ✅ Visibility states (тус тусдаа)
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showRepeat, setShowRepeat] = useState(false);

    // ✅ Clear input fields
    const clearFields = () => {
        setCurrentPassword('');
        setNewPassword('');
        setRepeatPassword('');
    };

    // ✅ Submit form
    const handleSubmit = async (e) => {
        e.preventDefault();

        // 🟡 1. Хоосон талбар шалгах
        if (!oldPassword || !newPassword || !repeatPassword) {
            dispatch({
                type: SET_ALERT,
                data: {
                    isShow: true,
                    type: 'warning',
                    message: t('warning.fill_all_info'),
                },
            });
            return;
        }

        // 🔴 2. Шинэ нууц үгүүд тохирох эсэх
        if (newPassword !== repeatPassword) {
            dispatch({
                type: SET_ALERT,
                data: {
                    isShow: true,
                    type: 'warning',
                    message: t('warning.not_match_password'),
                },
            });
            return;
        }

        try {
            // 🟢 3. Сервер рүү хүсэлт илгээх (жишээ: changePassword API)
            const payload = {
                oldPassword,
                newPassword,
            };

            const result = await changePassword(payload);

            if (result?.status === 'success') {
                dispatch({
                    type: SET_ALERT,
                    data: {
                        isShow: true,
                        type: 'success',
                        message: t('warning.password_changed'),
                    },
                });

                // 🧹 Талбар цэвэрлэх
                clearFields();
                handleClose();
            } else {
                dispatch({
                    type: SET_ALERT,
                    data: {
                        isShow: true,
                        type: 'error',
                        message:
                            t('error.invalid_current_password') ||
                            t('error.invalid_current_password'),
                    },
                });
                clearFields();
            }
        } catch (error) {
            console.error('Password change error:', error);
            dispatch({
                type: SET_ALERT,
                data: {
                    isShow: true,
                    type: 'error',
                    message: t('error.server_error'),
                },
            });
        }
    };

    const closePopUp = () => {
        clearFields();
        handleClose(false);
    };

    return (
        <Dialog
            open={open}
            onClose={closePopUp}
            fullScreen={sizes.sm}
            maxWidth="sm"
            fullWidth
        >
            {/* ✅ Header */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {t('button.change_password')}
                <IconButton onClick={closePopUp}>
                    <X />
                </IconButton>
            </DialogTitle>

            {/* ✅ Content */}
            <DialogContent>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5 mt-4 mb-8 mx-2 md:mx-10"
                >
                    {/* ✅ Current Password */}
                    <TextField
                        label={t('title.current_password')}
                        variant="outlined"
                        type={showCurrent ? 'text' : 'password'}
                        value={oldPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowCurrent((prev) => !prev)
                                        }
                                        edge="end"
                                    >
                                        {showCurrent ? (
                                            <Eye
                                                size={20}
                                                className="text-primary"
                                            />
                                        ) : (
                                            <EyeOff
                                                size={20}
                                                className="text-primary"
                                            />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* ✅ New Password */}
                    <TextField
                        label={t('title.new_password')}
                        variant="outlined"
                        type={showNew ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowNew((prev) => !prev)
                                        }
                                        edge="end"
                                    >
                                        {showNew ? (
                                            <Eye
                                                size={20}
                                                className="text-primary"
                                            />
                                        ) : (
                                            <EyeOff
                                                size={20}
                                                className="text-primary"
                                            />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* ✅ Repeat Password */}
                    <TextField
                        label={t('title.password_repeat')}
                        variant="outlined"
                        type={showRepeat ? 'text' : 'password'}
                        value={repeatPassword}
                        onChange={(e) => setRepeatPassword(e.target.value)}
                        fullWidth
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowRepeat((prev) => !prev)
                                        }
                                        edge="end"
                                    >
                                        {showRepeat ? (
                                            <Eye
                                                size={20}
                                                className="text-primary"
                                            />
                                        ) : (
                                            <EyeOff
                                                size={20}
                                                className="text-primary"
                                            />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* ✅ Submit */}
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        {t('title.save')}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PasswordChangeCompose;
